import { Product } from '@/context/types';
import { ReactNode, useCallback } from 'react';
import { motion } from 'framer-motion';
import NutritionLabel from '@/components/NutritionLabel';
import { Link, useNavigate } from 'react-router-dom';
import { useModalActions } from '@/components/Modals/useModalActions';
import { MODAL_KEY } from '@/constants';

interface ProductDetailsProps {
  product?: Product;
  loading?: boolean;
}

export default ({ product, loading = false }: ProductDetailsProps) => {
  if (!product) return <></>;
  const { openModal } = useModalActions(MODAL_KEY.CONFIRM_USE_DEFAULT_ADDRESS);
  const navigate = useNavigate();
  const handleToProductPage = useCallback(
    (e: any) => {
      e?.preventDefault();
      openModal({
        title: 'Unsaved Changes',
        message: (
          <span className="text-center font-display font-[500]">
            Oh no! You're leaving...
            <br />
            Are You Sure?
          </span>
        ),
        confirmText: 'Discard',
        cancelText: 'Finish Box',
        onConfirm: () => {
          navigate(`/product/${product.id}`);
        },
        onCancel: () => {},
      });
    },
    [product?.id, openModal],
  );

  return (
    <motion.div className="mt-8 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* 1. Hero Image Section */}
      <motion.div
        className="relative w-full max-w-sm aspect-square overflow-visible mb-8 group"
        initial={{ opacity: 0, x: 30, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      >
        {/* Ambient Glow behind the image to make it "pop" */}
        <div className="absolute inset-0 bg-brand-gold/10 blur-[80px] rounded-full scale-125 z-0 pointer-events-none" />
        {product.img?.url ? (
          <motion.img
            src={product.img.url}
            alt={product.img.altText || product.title}
            className="w-full h-full object-contain transition-transform duration-400 group-hover:scale-110 drop-shadow-[0_15px_15px_rgba(0,0,0,0.35)] will-change-transform pointer-events-auto"
            // Slight hover scale enhances the 3D effect without breaking the layout
            whileHover={{ y: -6, scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-brand-cream/30 text-text-muted font-display uppercase tracking-widest text-sm">
            {product.productType}
          </div>
        )}
      </motion.div>

      {/* 2. Content Section */}
      <div className="text-left w-full max-w-md px-4">
        {/* Category Label */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-brand-sage font-display font-bold text-[0.7rem] uppercase tracking-[0.2em]">
            {product.productType || 'Featured Selection'}
          </span>
          <div className="h-px flex-1 bg-brand-truffle/10" />
        </div>

        {/* Title - Matched tracking to list snippet */}

        <h2 className="font-display text-4xl text-text-heading mb-3 tracking-wider leading-tight hover:underline">
          {product.title}
        </h2>

        {/* 3. Refined Metadata Row (Consistent with List Snippet) */}
        <div className="flex flex-wrap items-center gap-y-2 mb-6">
          {/* Calorie Count with Snippet's Flame Icon */}
          {product?.nutritionFacts?.calories && (
            <div className="flex items-center text-text-muted text-xs font-[500] mr-3">
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="var(--color-brand-burgundy)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
              </svg>
              {product?.nutritionFacts?.calories} calories
            </div>
          )}

          {/* Dot Separator from Snippet */}
          {product?.nutritionFacts?.calories &&
            product?.allergens &&
            product.allergens.length > 0 && (
              <span className="text-brand-truffle/20 text-xs mr-3">&bull;</span>
            )}

          {/* Allergen Pills scaled from Snippet */}
          <div className="flex flex-wrap gap-1.5">
            {product?.allergens?.map((allergen: string) => (
              <span
                key={allergen}
                className="px-2 py-0.5 border border-brand-truffle/10 bg-brand-truffle/5 text-text-muted text-[10px] rounded-sm font-[700] uppercase tracking-widest"
              >
                {allergen}
              </span>
            ))}
          </div>
        </div>

        {/* 4. Description/Tags */}
        <p className="font-body text-text-muted text-sm leading-relaxed mb-6">
          {/* Placeholder for product description if available */}
          Our {product.title} is crafted with artisanal care, ensuring a perfect
          balance of flavor and texture in every bite.
        </p>

        <div className="flex flex-wrap gap-2">
          {product.tags?.reduce((elem: ReactNode[], tag) => {
            if (tag == 'groupable') return elem;
            const t = tag.replace('mix:', '');
            elem.push(
              <span
                key={t}
                className="px-3 py-1 bg-brand-cream border border-brand-truffle/5 text-brand-truffle/60 rounded-full text-[0.65rem] font-bold uppercase tracking-widest"
              >
                #{t}
              </span>,
            );
            return elem;
          }, [])}
        </div>
      </div>
    </motion.div>
  );
};
